import { SphereGeometry, ShaderMaterial, Color, BackSide, Vector3 } from 'three';
import { gsap } from 'gsap';
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import WebGLObject from '../_app/cuchillo/3D/WebGLObject';
import { BG_VERTEX } from './shaders/bg-vertex';
import { BG_FRAGMENT } from './shaders/bg-fragment';

const SPHERE_FACTOR = 5;
const RESEED_DURATION = 0.8; // seconds — must match GSAP duration in reseed()

export const BG_SETTINGS = {
  bgColor: '#ffffff',
  rectColor: '#000000',
  kill: 1,
  seed: 1.8,
  count: 14,
  blur: 0.01,
  globalIntensity: 0.32,
  intensityMin: 0.0,
  intensityMax: 0.21,
  widthMin: 0.082,
  widthMax: 0.137,
  heightMin: 0.157,
  heightMax: 0.453,
  timeScale: 0.58,
  lifeScale: 0.20,
  dispMin: 0.1,
  dispMax: 0.37,
  centerWidth: 0.5,
  rotationY: 0,
  lifeMin: 1.8,
  lifeMax: 8.6
};

export default class BGSphere {
  static _sphere = null;
  static _reseedTime = 0; // uTime value when the last seed change happened
  static _animBgColor = new Color(BG_SETTINGS.bgColor);
  static _animRectColor = new Color(BG_SETTINGS.rectColor);

  static init(dom) {
    const s = dom.offsetWidth * SPHERE_FACTOR;

    this._sphere = new WebGLObject({
      geometry: new SphereGeometry(),
      material: new ShaderMaterial({
        uniforms: {
          uBgColor: { value: new Color(BG_SETTINGS.bgColor) },
          uRectColor: { value: new Color(BG_SETTINGS.rectColor) },
          uSeed: { value: BG_SETTINGS.seed },
          uCount: { value: BG_SETTINGS.count },
          uWidthMin: { value: BG_SETTINGS.widthMin },
          uWidthMax: { value: BG_SETTINGS.widthMax },
          uHeightMin: { value: BG_SETTINGS.heightMin },
          uHeightMax: { value: BG_SETTINGS.heightMax },
          uBlur: { value: BG_SETTINGS.blur },
          uIntensityMin: { value: BG_SETTINGS.intensityMin },
          uIntensityMax: { value: BG_SETTINGS.intensityMax },
          uGlobalIntensity: { value: BG_SETTINGS.globalIntensity },
          uTime: { value: 0 },
          uTimeScale: { value: BG_SETTINGS.timeScale },
          uLifeScale: { value: BG_SETTINGS.lifeScale },
          uDispMin: { value: BG_SETTINGS.dispMin },
          uDispMax: { value: BG_SETTINGS.dispMax },
          uCenterWidth: { value: BG_SETTINGS.centerWidth },
          uUVScale: { value: 1 / SPHERE_FACTOR },
          uLifeMin: { value: BG_SETTINGS.lifeMin },
          uLifeMax: { value: BG_SETTINGS.lifeMax },
          uKill: { value: BG_SETTINGS.kill },
          uReseedTime: { value: 0 },
          uReseedDuration: { value: RESEED_DURATION },
        },
        vertexShader: BG_VERTEX,
        fragmentShader: BG_FRAGMENT,
        side: BackSide,
        depthTest: false,
        depthWrite: false
      }),
      size: new Vector3(s, s, s),
      dom
    });

    this._animBgColor.set(BG_SETTINGS.bgColor);
    this._animRectColor.set(BG_SETTINGS.rectColor);

    WebGLSketch.camera.add(this._sphere);
    this._sphere.init();
    this._sphere.renderOrder = -2;
    // UV (0.5,0.5) on SphereGeometry = +X direction. Camera looks toward -Z.
    // Rotating π/2 around Y aligns the UV center with the camera view.
    this._sphere.rotation.y = this._sphere.rot.y = Math.PI / 2;

    this._sphere.layers.set(1);
    this._sphere.mesh.layers.set(1);
  }

  static changePalette(bgCss, rectCss, bgDuration = 0.4, rectDuration = 1.4) {
    gsap.killTweensOf(this._animBgColor);
    gsap.killTweensOf(this._animRectColor);
    const tBg = new Color(bgCss);
    const tRect = new Color(rectCss);
    gsap.to(this._animBgColor, { r: tBg.r, g: tBg.g, b: tBg.b, duration: bgDuration, ease: 'power2.out' });
    gsap.to(this._animRectColor, { r: tRect.r, g: tRect.g, b: tRect.b, duration: rectDuration, ease: 'power2.out' });
  }

  static loop() {
    if (!this._sphere) return;
    const u = this._sphere.material.uniforms;
    u.uBgColor.value.copy(this._animBgColor);
    u.uRectColor.value.copy(this._animRectColor);
    u.uSeed.value = BG_SETTINGS.seed;
    u.uCount.value = BG_SETTINGS.count;
    u.uWidthMin.value = BG_SETTINGS.widthMin;
    u.uWidthMax.value = BG_SETTINGS.widthMax;
    u.uHeightMin.value = BG_SETTINGS.heightMin;
    u.uHeightMax.value = BG_SETTINGS.heightMax;
    u.uBlur.value = BG_SETTINGS.blur;
    u.uIntensityMin.value = BG_SETTINGS.intensityMin;
    u.uIntensityMax.value = BG_SETTINGS.intensityMax;
    u.uGlobalIntensity.value = BG_SETTINGS.globalIntensity;
    u.uTime.value = WebGLSketch.time();
    u.uTimeScale.value = BG_SETTINGS.timeScale;
    u.uLifeScale.value = BG_SETTINGS.lifeScale;
    u.uDispMin.value = BG_SETTINGS.dispMin;
    u.uDispMax.value = BG_SETTINGS.dispMax;
    u.uCenterWidth.value = BG_SETTINGS.centerWidth;
    u.uLifeMin.value = BG_SETTINGS.lifeMin;
    u.uLifeMax.value = BG_SETTINGS.lifeMax;
    u.uKill.value = BG_SETTINGS.kill;
    u.uReseedTime.value = this._reseedTime;
  }

  // Reveals rects from the initial hidden state (second half of reseed).
  // Each rect is born fresh from t=0 at a staggered time, giving a natural lifecycle fade-in.
  static revealRects(duration = RESEED_DURATION) {
    // El shader del BGSphere pinta uBgColor a tota la pantalla SEMPRE (alpha=1 fix, uKill
    // només apaga els rects) → cal que WebGLSketch NOMÉS renderitzi aquesta layer mentre
    // està realment activa (mostrant-se o fent el fade out), sinó queda un bloc de color
    // sòlid tapant el fons HTML per sempre un cop s'amaga.
    WebGLSketch.bgLayerActive = true;
    this._reseedTime = WebGLSketch.time();
    gsap.to(BG_SETTINGS, {
      kill: 0,
      duration,
      ease: 'power2.inOut'
    });
  }

  // Kills all rects and changes seed — no re-reveal (used when leaving the page).
  static killRects(duration = RESEED_DURATION) {
    gsap.killTweensOf(BG_SETTINGS, 'kill');
    BG_SETTINGS.seed = Math.random() * 100;
    this._reseedTime = WebGLSketch.time();
    gsap.to(BG_SETTINGS, {
      kill: 1,
      duration,
      ease: 'power2.inOut',
      onComplete: () => { WebGLSketch.bgLayerActive = false; } // fade out acabat → deixa de renderitzar la layer
    });
  }

  // Desynchronized reseed:
  // 1. kill 0→1 — rects die in random order (killFade per threshold)
  // 2. seed changes while all are invisible
  // 3. kill 1→0 — rects are born fresh from t=0, staggered by threshold
  static reseed(onSeedChange) {
    gsap.to(BG_SETTINGS, {
      kill: 1,
      duration: RESEED_DURATION,
      ease: 'power2.inOut',
      onComplete: () => {
        BG_SETTINGS.seed = Math.random() * 100;
        // Record the exact uTime of the seed change so the shader
        // can start each rect's fresh lifecycle from the right moment.
        this._reseedTime = WebGLSketch.time();

        if (onSeedChange) onSeedChange();

        gsap.to(BG_SETTINGS, {
          kill: 0,
          duration: RESEED_DURATION,
          ease: 'power2.inOut'
        });
      }
    });
  }

  static resize(dom) {
    if (!this._sphere) return;
    const s = dom.offsetWidth * SPHERE_FACTOR;
    this._sphere.resize(s, s, s);
  }

  static dispose() {
    if (!this._sphere) return;
    WebGLSketch.camera.remove(this._sphere);
    this._sphere.dispose();
    this._sphere = null;
  }
}
